/**
 * Global UI settings — theme, font, density, price mode, time formatting.
 *
 * Persisted to localStorage. Mirrors trading-bot-app's settings shape so
 * the User Settings page feels familiar.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { type SymbolFormat, DEFAULT_SYMBOL_FORMAT } from '@/lib/symbolFormatter'

export type { SymbolFormat }
export type Theme = 'light' | 'dark' | 'system'
export type Density = 'compact' | 'comfortable' | 'spacious'
export type PriceMode = 'last' | 'mid' | 'bid' | 'ask'
export type DateFormat = 'iso' | 'us' | 'eu' | 'long'
export type TimeFormat = '24h' | '12h'
export type FontSize = 'sm' | 'md' | 'lg'
export type SymbolFont =
  | 'mono' | 'jetbrains' | 'fira' | 'source'
  | 'consolas' | 'roboto' | 'inter' | 'system'

export interface SymbolFontOption {
  id: SymbolFont
  label: string
  fontFamily: string
  description: string
}

/**
 * The trading-bot-app's full 8-font menu, stacks verbatim. The four Google
 * fonts (JetBrains Mono, Fira Code, Source Code Pro, Roboto Mono) and Inter
 * are bundled via @fontsource and lazy-loaded when selected, so every
 * option actually renders — the bot relied on locally-installed fonts.
 * Consolas has no bundlable webfont; it falls back to the system monospace
 * on non-Windows machines (bot-identical behavior).
 */
export const SYMBOL_FONT_OPTIONS: SymbolFontOption[] = [
  {
    id: 'mono',
    label: 'System Monospace',
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
    description: 'Default monospace font',
  },
  {
    id: 'jetbrains',
    label: 'JetBrains Mono',
    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
    description: 'Developer-focused monospace font',
  },
  {
    id: 'fira',
    label: 'Fira Code',
    fontFamily: '"Fira Code", ui-monospace, monospace',
    description: 'Programming font with ligatures',
  },
  {
    id: 'source',
    label: 'Source Code Pro',
    fontFamily: '"Source Code Pro", ui-monospace, monospace',
    description: 'Adobe open source monospace',
  },
  {
    id: 'consolas',
    label: 'Consolas',
    fontFamily: 'Consolas, ui-monospace, monospace',
    description: 'Windows monospace font',
  },
  {
    id: 'roboto',
    label: 'Roboto Mono',
    fontFamily: '"Roboto Mono", ui-monospace, monospace',
    description: 'Google monospace font',
  },
  {
    id: 'inter',
    label: 'Inter',
    fontFamily: 'Inter, system-ui, sans-serif',
    description: 'Modern sans-serif font',
  },
  {
    id: 'system',
    label: 'System Default',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    description: 'System UI font',
  },
]

/** Display-timezone choices (bot list, verbatim). */
export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Phoenix', label: 'Arizona (Phoenix)' },
  { value: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Europe/Zurich', label: 'Zurich' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Australia/Sydney', label: 'Sydney' },
]

export const FONT_SIZE_PX: Record<FontSize, number> = {
  sm: 14,
  md: 16,
  lg: 18,
}

// Bundled webfonts, lazy-loaded on selection (and on the settings page for
// previews). System Monospace / Consolas / System Default need no download.
const WEBFONT_LOADERS: Partial<Record<SymbolFont, () => Promise<unknown>>> = {
  jetbrains: () => import('@fontsource/jetbrains-mono/index.css'),
  fira: () => import('@fontsource/fira-code/index.css'),
  source: () => import('@fontsource/source-code-pro/index.css'),
  roboto: () => import('@fontsource/roboto-mono/index.css'),
  inter: () => import('@fontsource/inter/index.css'),
}
const _loadedFonts = new Set<string>()

export function loadSymbolWebfont(font: SymbolFont): void {
  const loader = WEBFONT_LOADERS[font]
  if (!loader || _loadedFonts.has(font)) return
  _loadedFonts.add(font)
  loader().catch(() => { _loadedFonts.delete(font) })
}

/**
 * Look up the CSS font-family stack for a SymbolFont id.
 * Falls back to the first option (system monospace) if unknown.
 */
export function getSymbolFontFamily(font: SymbolFont): string {
  const option = SYMBOL_FONT_OPTIONS.find((o) => o.id === font)
  return option?.fontFamily ?? SYMBOL_FONT_OPTIONS[0].fontFamily
}

interface SettingsState {
  theme: Theme
  sidebarCollapsed: boolean
  density: Density
  priceMode: PriceMode
  dateFormat: DateFormat
  timeFormat: TimeFormat
  fontSize: FontSize
  symbolFont: SymbolFont
  symbolFormat: SymbolFormat
  timezone: string  // IANA tz, e.g. 'America/New_York'

  setTheme: (theme: Theme) => void
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  setDensity: (density: Density) => void
  setPriceMode: (mode: PriceMode) => void
  setDateFormat: (f: DateFormat) => void
  setTimeFormat: (f: TimeFormat) => void
  setFontSize: (s: FontSize) => void
  setSymbolFont: (f: SymbolFont) => void
  setSymbolFormat: (f: SymbolFormat) => void
  setTimezone: (tz: string) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      theme: 'dark',
      sidebarCollapsed: false,
      density: 'comfortable',
      priceMode: 'last',
      dateFormat: 'iso',
      timeFormat: '24h',
      fontSize: 'md',
      symbolFont: 'mono',
      symbolFormat: DEFAULT_SYMBOL_FORMAT,
      timezone: 'America/New_York',
      setTheme: (theme) => set({ theme }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setDensity: (density) => set({ density }),
      setPriceMode: (priceMode) => set({ priceMode }),
      setDateFormat: (dateFormat) => set({ dateFormat }),
      setTimeFormat: (timeFormat) => set({ timeFormat }),
      setFontSize: (fontSize) => set({ fontSize }),
      setSymbolFont: (symbolFont) => set({ symbolFont }),
      setSymbolFormat: (symbolFormat) => set({ symbolFormat }),
      setTimezone: (timezone) => set({ timezone }),
    }),
    { name: 'compass.settings' },
  ),
)
